import React from 'react';
import { Loader } from 'lucide-react';
import { CATEGORY_OPTIONS } from '../constants/categories';

type Props = {
  initial?: any;
  onCancel?: () => void;
  onSubmit: (data: any) => Promise<void> | void;
};

const MONTHS = ['jan.', 'fev.', 'mar.', 'abr.', 'mai.', 'jun.', 'jul.', 'ago.', 'set.', 'out.', 'nov.', 'dez.'];

export default function InstallmentForm({ initial, onCancel, onSubmit }: Props) {
  const [form, setForm] = React.useState(() => ({
    description: initial?.description || '',
    category: initial?.category || CATEGORY_OPTIONS[0],
    installmentValue: initial?.installmentValue ?? 0,
    installmentCount: initial?.installmentCount ?? 1,
    startMonth: initial?.startMonth ?? new Date().getMonth() + 1,
    startYear: initial?.startYear ?? new Date().getFullYear(),
  }));
  const [isLoading, setIsLoading] = React.useState(false);

  const [displayParcel, setDisplayParcel] = React.useState(() =>
    form.installmentValue
      ? new Intl.NumberFormat(navigator.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(form.installmentValue)
      : ''
  );

  const handleChange = (key: string, value: any) => setForm((s) => ({ ...s, [key]: value }));

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat(navigator.language, { style: 'currency', currency: 'BRL' }).format(value || 0);

  const handleMoneyChange = (
    raw: string,
    setDisplay: React.Dispatch<React.SetStateAction<string>>,
    onValue: (num: number) => void
  ) => {
    const digits = raw.replace(/[^\d]/g, '');
    if (!digits) {
      setDisplay('');
      onValue(0);
      return;
    }
    const num = parseFloat(digits) / 100;
    setDisplay(new Intl.NumberFormat(navigator.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num));
    onValue(num);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    try {
      const count = Number(form.installmentCount) || 1;
      const totalFromParcel = (form.installmentValue || 0) * count;
      const parcelFromTotal = form.installmentValue;

      const payload = {
        description: form.description,
        category: form.category,
        totalAmount: Number(totalFromParcel),
        installmentValue: Number(parcelFromTotal),
        installmentCount: count,
        startMonth: Number(form.startMonth),
        startYear: Number(form.startYear),
      };
      await onSubmit(payload);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-1">Descrição da Compra</label>
        <input
          placeholder="Ex: Notebook Dell"
          value={form.description}
          onChange={(e) => handleChange('description', e.target.value)}
          className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 text-gray-900"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-900 mb-1">Categoria</label>
        <select
          value={form.category}
          onChange={(e) => handleChange('category', e.target.value)}
          className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 appearance-none text-gray-900 bg-white"
        >
          {CATEGORY_OPTIONS.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="relative">
          <label className="block text-sm font-medium text-gray-900 mb-1">Valor da Parcela</label>
          <span className="absolute left-3 top-9 text-gray-500">
            {formatCurrency(0).replace(/[\d.,\s]/g, '')}
          </span>
          <input
            type="text"
            placeholder="0,00"
            value={displayParcel}
            onChange={(e) => handleMoneyChange(e.target.value, setDisplayParcel, (n) => handleChange('installmentValue', n))}
            className="w-full border border-gray-300 pl-10 pr-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 text-gray-900"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">Número de Parcelas</label>
          <input
            type="number"
            min={1}
            value={form.installmentCount}
            onChange={(e) => handleChange('installmentCount', Number(e.target.value))}
            className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 text-gray-900"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">Mês Início</label>
          <select
            value={form.startMonth}
            onChange={(e) => handleChange('startMonth', Number(e.target.value))}
            className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 appearance-none text-gray-900 bg-white"
          >
            {MONTHS.map((m, idx) => (
              <option key={m} value={idx + 1}>
                {m}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">Ano</label>
          <input
            type="number"
            value={form.startYear}
            onChange={(e) => handleChange('startYear', Number(e.target.value))}
            className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 text-gray-900"
          />
        </div>
        <div className="flex items-end">
          <div className="w-full bg-blue-50 text-blue-700 font-semibold px-3 py-2 rounded-md border border-blue-100">
            Valor total: {formatCurrency((form.installmentValue || 0) * (form.installmentCount || 1))}
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
        )}
        <button
          type="submit"
          disabled={isLoading}
          className="flex-1 px-4 py-2 bg-gray-900 text-white rounded-md font-medium hover:bg-gray-800 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading && <Loader className="w-4 h-4 animate-spin" />}
          {isLoading ? 'Processando...' : (initial ? 'Salvar' : 'Criar Parcelas')}
        </button>
      </div>
    </form>
  );
}
